import { Scissors, Sparkles } from "lucide-react";
import type { ClusterView } from "../../lib/api.js";

/**
 * Horizontal strip of cluster cards. Clicking one filters the keyword table.
 * The "All" pseudo-card resets the filter. The small ✨ button on each card
 * opens the cluster-expansion dialog (DFS + LLM expansion + suggestion review);
 * the ✂ button opens the split dialog (bucket → one-page sub-clusters).
 */
export function ClusterStrip({
  clusters,
  loading,
  selectedId,
  onSelect,
  onExpand,
  onSplit,
}: {
  clusters: ClusterView[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onExpand: (cluster: ClusterView) => void;
  onSplit: (cluster: ClusterView) => void;
}) {
  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-24 w-56 shrink-0 animate-pulse rounded-lg bg-line" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      <AllCard active={selectedId === null} onClick={() => onSelect(null)} count={clusters.length} />
      {clusters.map((c) => (
        <ClusterCard
          key={c.id}
          cluster={c}
          active={selectedId === c.id}
          onClick={() => onSelect(selectedId === c.id ? null : c.id)}
          onExpand={() => onExpand(c)}
          onSplit={() => onSplit(c)}
        />
      ))}
    </div>
  );
}

function AllCard({ active, onClick, count }: { active: boolean; onClick: () => void; count: number }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-lg border bg-white px-4 py-3 text-left transition w-44 ${
        active ? "border-brand ring-2 ring-brand/30" : "border-line hover:border-muted"
      }`}
    >
      <div className="text-sm font-semibold">All clusters</div>
      <div className="mt-2 text-2xl font-semibold text-brand">{count}</div>
      <div className="text-xs uppercase tracking-wide text-muted">total</div>
    </button>
  );
}

function ClusterCard({
  cluster,
  active,
  onClick,
  onExpand,
  onSplit,
}: {
  cluster: ClusterView;
  active: boolean;
  onClick: () => void;
  onExpand: () => void;
  onSplit: () => void;
}) {
  const target = cluster.targetArticleCount ?? 0;
  const current = cluster.currentArticleCount;
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;

  return (
    <div
      className={`group relative shrink-0 rounded-lg border bg-white transition w-56 ${
        active ? "border-brand ring-2 ring-brand/30" : "border-line hover:border-muted"
      }`}
    >
      <button onClick={onClick} className="block w-full px-4 py-3 text-left">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{cluster.nameEn}</div>
            <div className="truncate text-xs text-muted" dir="rtl">{cluster.nameHe}</div>
          </div>
          <PriorityDot priority={cluster.businessPriority} />
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-xl font-semibold">{current}</span>
          {target > 0 && <span className="text-xs text-muted">/ {target} target</span>}
        </div>
        {target > 0 && (
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-line">
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        <div className="mt-2 text-[10px] uppercase tracking-wide text-muted">
          {cluster.pillarStatus}
        </div>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onExpand(); }}
        title="Expand this cluster with AI-suggested keywords"
        aria-label={`Expand ${cluster.nameEn}`}
        className="absolute right-1.5 top-1.5 hidden rounded-md bg-white/90 p-1 text-muted shadow-sm ring-1 ring-line hover:bg-brand/10 hover:text-brand group-hover:block"
      >
        <Sparkles className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onSplit(); }}
        title="Split this cluster into one-page sub-clusters"
        aria-label={`Split ${cluster.nameEn}`}
        className="absolute right-9 top-1.5 hidden rounded-md bg-white/90 p-1 text-muted shadow-sm ring-1 ring-line hover:bg-brand/10 hover:text-brand group-hover:block"
      >
        <Scissors className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function PriorityDot({ priority }: { priority: ClusterView["businessPriority"] }) {
  const color =
    priority === "urgent" ? "bg-danger" :
    priority === "high" ? "bg-warn" :
    priority === "medium" ? "bg-accent" :
    "bg-line";
  return <span className={`h-2 w-2 shrink-0 rounded-full ${color}`} title={priority} />;
}
